import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { setNewLocation } from '../../../../features/auth/authSlice';
import { useUpdateCurrentMasterLocationMutation } from '../../../../features/master/masterApiSlice';

const GEO_OPTIONS = { timeout: 10000, maximumAge: 300000, enableHighAccuracy: false };

const LocationPermission = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const [updateCurrentMasterLocation] = useUpdateCurrentMasterLocationMutation();
    const [isChecked, setIsChecked] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [status, setStatus] = useState('unknown');
    const [errorCode, setErrorCode] = useState(null);

    const saveLocation = async ({ coords }) => {
        const newCoords = { lat: coords.latitude, lng: coords.longitude };

        try {
            const result = await updateCurrentMasterLocation(newCoords).unwrap();
            if (!result?.success) throw new Error('Location was not saved');
            dispatch(setNewLocation(newCoords));
            setIsChecked(true);
            setStatus('determined');
            setErrorCode(null);
        } catch (error) {
            console.error(error);
            setIsChecked(false);
            setStatus('saveError');
        } finally {
            setIsLoading(false);
        }
    };

    const handleError = error => {
        setIsChecked(false);
        setIsLoading(false);
        setErrorCode(error?.code || 'UNAVAILABLE');
        setStatus('error');
    };

    const requestLocation = () => {
        if (isLoading) return;
        if (!navigator.geolocation) {
            handleError({ code: 'UNSUPPORTED' });
            return;
        }

        setIsLoading(true);
        setStatus('requesting');
        setErrorCode(null);
        navigator.geolocation.getCurrentPosition(saveLocation, handleError, GEO_OPTIONS);
    };

    useEffect(() => {
        if (!navigator.permissions?.query) return undefined;

        let permissionStatus;
        const onPermissionChange = () => {
            if (permissionStatus.state === 'granted') requestLocation();
            if (permissionStatus.state === 'denied') handleError({ code: 1 });
            if (permissionStatus.state === 'prompt') {
                setIsChecked(false);
                setStatus('unknown');
                setErrorCode(null);
            }
        };

        navigator.permissions.query({ name: 'geolocation' }).then(result => {
            permissionStatus = result;
            if (result.state === 'granted') requestLocation();
            if (result.state === 'denied') handleError({ code: 1 });
            result.addEventListener('change', onPermissionChange);
        }).catch(() => {});

        return () => permissionStatus?.removeEventListener?.('change', onPermissionChange);
    }, []);

    const errorMessageKey = errorCode === 1
        ? 'GeolocationPermissionDenied'
        : errorCode === 2
            ? 'GeolocationPositionUnavailable'
            : errorCode === 3
                ? 'GeolocationTimeout'
                : 'GeolocationUnsupported';

    const statusKey = status === 'determined'
        ? 'LocationDetermined'
        : status === 'requesting'
            ? 'DeterminingLocation'
            : 'LocationNotDetermined';

    return <div className="col-xl-12">
        <div className="dashboard-box" style={{ boxShadow: !isChecked ? '0 2px 8px rgba(255,0,0,0.2)' : '' }}>
            <div className="headline" style={{ borderBottomColor: !isChecked ? 'rgba(255,0,0,0.2)' : '' }}>
                <h3><i style={{ color: !isChecked ? 'red' : '' }} className="icon-material-outline-my-location"></i> {t('ViewGeolocation')}</h3>
            </div>

            <div className="content with-padding">
                <div className="row">
                    <div className="col-xl-12">
                        <p title="explanation">
                            <i className="icon-feather-info"></i>&nbsp;{t('ViewGeolocationTooltip')}
                        </p>

                        <div className="switches-list">
                            <div className="switch-container">
                                <label className="switch" style={{ opacity: isLoading ? 0.65 : 1 }}>
                                    <input onChange={requestLocation} type="checkbox" checked={isChecked} disabled={isLoading} />
                                    <span className="switch-button"></span>
                                    {isLoading ? t('DeterminingLocation') : t('DetermineMyLocation')}
                                </label>
                            </div>
                        </div>

                        <p style={{ marginTop: 12, maxWidth: 'inherit' }}>
                            <strong>{t('LocationStatus')}:</strong> {t(statusKey)}
                        </p>

                        {status === 'saveError' && <p style={{ color: '#b42318' }}>{t('GeolocationSaveError')}</p>}
                        {status === 'error' && <div style={{ color: '#b42318', marginTop: 10 }}>
                            <p><strong>{t(errorMessageKey)}</strong></p>
                            <p>{t('GeolocationBrowserInstructions')}</p>
                            <ul style={{ marginTop: 8 }}>
                                <li>{t('GeolocationSafariInstructions')}</li>
                                <li>{t('GeolocationMacInstructions')}</li>
                                <li>{t('GeolocationChromeInstructions')}</li>
                            </ul>
                        </div>}
                    </div>
                </div>
            </div>
        </div>
    </div>;
};

export default LocationPermission;
